import { DESCRIPTIONS, TYPE_LABELS } from '../data/megaPokemon.js';
import bundledPokemon from '../data/megaPokemonDetails.json';

const API_BASE = 'https://pokeapi.co/api/v2/pokemon';
const CACHE_KEY = 'mega-pokedex:v2';
const CACHE_TTL = 1000 * 60 * 60 * 24 * 7;

const titleCase = (value) =>
  value
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const readCache = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(CACHE_KEY));
    if (!stored || Date.now() - stored.savedAt > CACHE_TTL) return new Map();
    return new Map(stored.items.map((item) => [item.slug, item]));
  } catch {
    return new Map();
  }
};

const writeCache = (items) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), items }));
  } catch {
    // El almacenamiento puede estar deshabilitado. La app sigue funcionando sin caché.
  }
};

const requestPokemon = async (entry) => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12000);

  try {
    const response = await fetch(`${API_BASE}/${entry.slug}`, { signal: controller.signal });
    if (!response.ok) throw new Error(`PokeAPI respondió ${response.status}`);
    const data = await response.json();
    const types = data.types.map(({ type }) => type.name);
    const primaryType = types[0] ?? 'normal';
    const image =
      data.sprites.other?.['official-artwork']?.front_default ??
      data.sprites.other?.home?.front_default ??
      data.sprites.front_default;

    return {
      ...entry,
      id: data.id,
      image,
      types,
      typeLabels: types.map((type) => TYPE_LABELS[type] ?? titleCase(type)),
      primaryType,
      height: data.height / 10,
      weight: data.weight / 10,
      ability: titleCase(data.abilities[0]?.ability.name ?? 'Desconocida'),
      stats: data.stats.map(({ base_stat: value, stat }) => ({ name: stat.name, value })),
      description:
        DESCRIPTIONS[entry.slug] ??
        `${entry.name} concentra la megaenergía para reforzar su poder y llevar sus habilidades de tipo ${TYPE_LABELS[primaryType] ?? titleCase(primaryType)} a otro nivel.`,
    };
  } finally {
    window.clearTimeout(timeout);
  }
};

const runPool = async (entries, limit, worker) => {
  const results = new Array(entries.length);
  let nextIndex = 0;

  const runners = Array.from({ length: Math.min(limit, entries.length) }, async () => {
    while (nextIndex < entries.length) {
      const index = nextIndex;
      nextIndex += 1;
      try {
        results[index] = await worker(entries[index]);
      } catch (error) {
        console.warn(`No se pudo cargar ${entries[index].slug}:`, error);
        results[index] = null;
      }
    }
  });

  await Promise.all(runners);
  return results;
};

export const loadMegaPokemon = async (entries) => {
  const bundled = new Map(bundledPokemon.map((pokemon) => [pokemon.slug, pokemon]));
  const cache = readCache();
  bundled.forEach((pokemon, slug) => cache.set(slug, pokemon));
  const missing = entries.filter((entry) => !cache.has(entry.slug));

  if (missing.length) {
    const downloaded = (await runPool(missing, 8, requestPokemon)).filter(Boolean);
    downloaded.forEach((pokemon) => cache.set(pokemon.slug, pokemon));
  }

  const items = entries.map((entry) => cache.get(entry.slug)).filter(Boolean);
  if (items.length) writeCache(items);
  return items;
};
